import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Paper, Box, TextField, Typography, Button, LinearProgress } from "@mui/material";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { InformacoesForm } from "shared/interfaces";
import { useGetInformacaoQuery, useUpdateInformacaoMutation } from "shared/features/api/informacao/informacaoSlice";
import { toast } from "react-toastify";
import { updateInformacao } from "shared/schemas/updateInformacoes";

const normalizeHtml = (html: string) => {
  if (!html) return "";
  
  return html
    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><')
    .replace(/<section>/g, '')
    .replace(/<\/section>/g, '')
    .replace(/<p>\s*<br>\s*<\/p>/g, '')
    .trim();
};

export const ConfigInformacoes: React.FC = () => {
  const { data: informacoes, isLoading: carregandoInformacoes } = useGetInformacaoQuery();
  const [atualizarInformacoes, {isLoading: carregandoAtualizacaoInformacoes}] = useUpdateInformacaoMutation();
  const valoresIniciais = useRef<InformacoesForm>({ titulo: "", conteudo: "", linkEdital: "" });
  const [isAlterado, setIsAlterado] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<InformacoesForm>({
    resolver: yupResolver(updateInformacao),
    defaultValues: {
      titulo: "",
      conteudo: "",
      linkEdital: ""
    }
  });

  const tituloValue = watch("titulo");
  const conteudoValue = watch("conteudo");
  const linkEditalValue = watch("linkEdital");

  useEffect(() => {
    if (informacoes) {
      const conteudoNormalizado = normalizeHtml(informacoes.conteudo || "");
      
      reset({
        titulo: informacoes.titulo || "",
        conteudo: conteudoNormalizado,
        linkEdital: informacoes.linkEdital || ""
      });

      valoresIniciais.current = {
        titulo: informacoes.titulo || "",
        conteudo: conteudoNormalizado,
        linkEdital: informacoes.linkEdital || ""
      };
      setIsAlterado(false);
    }
  }, [informacoes, reset]);

  useEffect(() => {
    const tituloIgual = tituloValue === valoresIniciais.current.titulo;
    const conteudoIgual = normalizeHtml(conteudoValue || "") === valoresIniciais.current.conteudo;
    const linkIgual = linkEditalValue === valoresIniciais.current.linkEdital;

    setIsAlterado(!(tituloIgual && conteudoIgual && linkIgual));
  }, [tituloValue, conteudoValue, linkEditalValue]);

  const onSubmit = async (data: InformacoesForm) => {
    console.log("Dados enviados:", data);
    
    valoresIniciais.current = {
      titulo: data.titulo,
      conteudo: normalizeHtml(data.conteudo || ""),
      linkEdital: data.linkEdital
    };
    setIsAlterado(false);
    
    await toast.promise(
        atualizarInformacoes(data).unwrap(),
        {
          pending: "Carregando...",
          success: `Informações do Vem Ser alteradas com sucesso!`,
          error: {
            render({ data }) {
              const response = data as {
                data?: {
                  errors?: string[];
                  message?: string;
                };
              };

              const errorMessage =
                response.data?.errors?.[0] ||
                response.data?.message ||
                `Erro ao alterar informações do Vem Ser.`;
              return errorMessage;
            },
          },
        }
    )
    
  };

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 3, width: "100%", maxWidth: 1000 }}>
        {carregandoInformacoes ? (
          <LinearProgress />
        ) : (
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Controller
                name="titulo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Título"
                    fullWidth
                    margin="normal"
                    error={!!errors.titulo}
                    helperText={errors.titulo?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} lg={6}>
              <Controller
                name="linkEdital"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Link edital"
                    fullWidth
                    margin="normal"
                    error={!!errors.linkEdital}
                    helperText={errors.linkEdital?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Conteúdo
              </Typography>
              <Controller
                name="conteudo"
                control={control}
                render={({ field }) => (
                  <>
                    <Box sx={{ height: "400px", mb: 1 }}>
                      <ReactQuill
                        theme="snow"
                        value={field.value || ""}
                        onChange={(value) => {
                          setValue("conteudo", value, {
                            shouldDirty: true,
                            shouldValidate: true
                          });
                        }}
                        style={{ height: "100%" }}
                      />
                    </Box>
                    {errors.conteudo && (
                      <Typography
                        color="error"
                        variant="body2"
                        marginTop={6}
                      >
                        {errors.conteudo.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item sx={{ mt: 4 }} xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!isAlterado || !isValid || carregandoAtualizacaoInformacoes}
              >
                Atualizar
              </Button>
            </Grid>
          </Grid>
          </Box>
        )}
      </Paper>
    </Grid>
  );
};